import { ApolloError, NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import {
  ErrorBoundary,
  ErrorRetry,
  ProjectAppTypes,
  SuspenseLoader,
  useEnvProvider,
  useName,
} from '@bechtel/client-common';
import { GET_USER_BY_IDs, UPDATE_USER } from '@bechtel/client-gql';
import { Box, Spinner, SpinnerSize, Text, ToastAction, ToastType, useToastsDispatch } from '@bechtel/gds';
import { UpdateUserError, UpdateUserInput, UpdateUserPayload, User } from '@bechtel/types';
import { get, map } from 'lodash';
import { Suspense, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
//@ts-ignore needed for declaration generation
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/empty-state_user-detail.svg';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { array, object } from 'yup';
import { FormikState } from '~/user-admin/users/form-state';
import { UserInfoDetail } from './detail';
import { UserUpdateError } from './error';
import { UserDetailContainerStyled } from './styled';

interface RouteParams {
  id: string;
}

const initialValues: FormikState = {
  roles: [],
  disciplines: [],
  company: null,
  facilities: [],
  isActive: false,
  jobTitle: '',
  becimsId: '',
  firstName: '',
  lastName: '',
  lobEmployeeId: '',
  bun: '',
};

export const UserDetail = () => {
  const { id } = useParams<RouteParams>();
  const { envConfig } = useEnvProvider();
  const [updateUserError, setUpdateUserError] = useState<UpdateUserError | ApolloError | null>(null);
  const { getFullName } = useName();
  const decodedId = id && id !== 'user-admin' ? decodeURIComponent(id) : '';
  const dispatchToast = useToastsDispatch();
  const [formikInitialValues, setFormikInitialValues] = useState<FormikState>(initialValues);
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  const [loadUserByIds, { loading, error, data, refetch, networkStatus }] = useLazyQuery<{ usersByIds: User[] }>(
    GET_USER_BY_IDs,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );

  const usersList: User[] = get(data, 'usersByIds', []);
  const userDetail = usersList.length > 0 ? usersList[0] : undefined;
  const userFulllName = userDetail ? getFullName(userDetail.firstName, userDetail.lastName) : '';
  let form;

  const editUserValidationSchema = object().shape({
    roles: array()
      .required()
      .min(1, t`Select at least 1 ${isPFAppType ? 'PipeFighter' : 'ArcStryker'} Role`),
  });

  const UserUpdateToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          {`${userFulllName} ${t`Profile successfully updated.`}`}
        </Text>
      </Box>
    );
  };

  const createSuccessToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.INFO,
      content: UserUpdateToast,
      autoDismissTimeout: 5000,
    },
  };

  useEffect(() => {
    if (!decodedId) return;
    loadUserByIds({
      variables: {
        ids: [decodedId],
      },
    });
  }, [decodedId, loadUserByIds]);

  useEffect(() => {
    if (form) form.handleReset();
    if (!userDetail || !userDetail.id) return;
    const innerInitialValues = {
      ...initialValues,
      isActive: userDetail?.isActive ? userDetail.isActive : false,
      roles: userDetail?.roles,
      disciplines: userDetail?.disciplines ? userDetail.disciplines : [],
      facilities: userDetail?.facilities ? userDetail.facilities : [],
      jobTitle: userDetail?.crewMember?.subGroupDescription ? userDetail?.crewMember?.subGroupDescription : '',
      becimsId: userDetail?.becImsId ? userDetail.becImsId.trim() : '',
      company: userDetail?.company ? userDetail.company : null,
      firstName: userDetail?.firstName ? userDetail.firstName : null,
      lastName: userDetail?.lastName ? userDetail.lastName : null,
      lobEmployeeId: userDetail?.etrackId ? userDetail.etrackId : '',
      bun: userDetail?.bun ? userDetail.bun.trim() : '',
    };
    //TODO: Formik rendering with old initialValues. Analyse the formik to remove the state.
    setFormikInitialValues(innerInitialValues);
  }, [userDetail]);

  const [updateUser, { loading: mutationLoading }] = useMutation<
    { updateUserPayload: UpdateUserPayload },
    { input: UpdateUserInput }
  >(UPDATE_USER, {
    onCompleted: (data) => {
      const updatedUserError = get(data, 'updateUser.error');
      if (updatedUserError) {
        setUpdateUserError(updatedUserError);
      } else {
        setUpdateUserError(null);
        dispatchToast(createSuccessToast);
      }
    },
    onError: (error) => {
      setUpdateUserError(error);
    },
    refetchQueries: ['users'],
  });

  if (!decodedId)
    return (
      <UserDetailContainerStyled data-testid="user-detail-empty">
        <Box
          flexDirection={{ sm: 'column' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          style={{ height: '100%' }}
        >
          <Box pb={{ sm: '600' }} px={{ sm: '600' }}>
            <Text family="graphik" color="grey.1000" size="250">
              <Trans>Select a user to see the details.</Trans>
            </Text>
          </Box>
          <Box pb={{ sm: '900' }} px={{ sm: '600' }}>
            <Text family="graphik" size="250" color="grey.1000">
              <Trans>You can search for a user by name or unique ID.</Trans>
            </Text>
          </Box>
          <PackageIcon />
        </Box>
      </UserDetailContainerStyled>
    );
  return (
    <Formik
      enableReinitialize={true}
      initialValues={formikInitialValues}
      validationSchema={editUserValidationSchema}
      onSubmit={async (values: FormikState) => {
        const input: UpdateUserInput = {
          userId: userDetail?.id ? userDetail?.id : '',
          roleIds: map(values.roles, 'id'),
          workPackageDisciplineIds: map(values.disciplines, 'id'),
          facilityIds: map(values.facilities, 'id'),
          companyId: values.company?.id,
          becImsId: values.becimsId?.trim(),
          isActive: values.isActive,
        };
        updateUser({
          variables: { input },
        });
      }}
    >
      {(formikProps: FormikProps<FormikState>) => {
        form = formikProps;
        return (
          <UserDetailContainerStyled>
            {(networkStatus === NetworkStatus.refetch || loading) && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                style={{ height: '100%', width: '100%' }}
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
            {!loading && error && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                style={{ height: '100%', width: '100%' }}
                data-testid="user-detail-retry"
              >
                <ErrorRetry refetch={refetch}>
                  <Trans>There was a problem loading user detail. Please click the button below to try again.</Trans>
                </ErrorRetry>
              </Box>
            )}
            {!loading && !error && userDetail && (
              <UserInfoDetail
                userDetail={userDetail}
                mutationLoading={mutationLoading}
                formikProps={formikProps}
                isNewUser={false}
                isEditUser={decodedId ? true : false}
              />
            )}
            {updateUserError && (
              <ErrorBoundary>
                <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                  <UserUpdateError
                    updateUserError={updateUserError}
                    close={() => {
                      setUpdateUserError(null);
                    }}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </UserDetailContainerStyled>
        );
      }}
    </Formik>
  );
};
